/*
	this is the User type we use on the frontend and when passing data between backend and frontend.
	the backend has a more complete User type, see backend/task-handler/shared/src/entity/user.ts. 

	*	trial: new user, no subscription, below trial group limit. 
		everything enabled, as if they were a paid user. 
		full group matching, new groups have `status == vested` (see also MembershipStatus). 
	*	expired-trial: new user, reached trial group limit, never had a paid subscription. 
		limited group matching, new groups have `status == limited`. 
	*	paid-trial: has an active subscription, but is also still below the trial group limit. 
		full group matching, new groups have `status == vested`. 
	*	paid: active subscription (beyond trial group limit). 
		full group matching, new groups have `status == subscription`
	*	cancelled: used to have a paid subscription, but cancelled. 
		limited matching, readonly groups -- almost like expired-trial, 
		but added to groups less frequently (canJoinAfter is much longer after a group match). 
	*	deleted: user deleted their account. marked as deleted, can't login, can't be added to groups, 
		but their messages and group memberships are kept in the db (for 30 days, then purged). 
	*	purged: user deleted their account. marked as purged, can't login, can't be added to groups, 
		and their messages and group memberships are deleted from the db. 

	*	limited group matching: 
		at least half the group must be linked. if we have enough linked users, 
		then high-ranking unlinked users can be added. 
	*	readonly groups: 
		the user can read messages in the group, but not send messages. 
		in place of the message input field, we show a link to settings / subscriptions. 
*/
export type AccountStatus = "trial" | "expired-trial" | "paid-trial" | "paid" | "cancelled" | "deleted" | "purged"

export type MembershipStatus = "vested" | "subscription" | "limited"

export interface User {
	userId: string
	email: string
	isDeveloper: boolean
	name?: string
	location: { lat: number; lng: number }
	introduction?: string
	color?: string
	accountStatus?: AccountStatus
	deletedAt?: number
	canPurgeAfter?: number
	//	false if the user is in an area where we don't yet offer subscriptions
	//	(area not supported, or too few users). undefined treated as true.
	canSubscribe?: boolean
}

//	Returns a non-empty display string for a user. Empty/whitespace-only `name` is
//	a valid persisted state (see signup flow + backend/lambda/create-user/src/main.ts),
//	so callers that render to the UI must fall back to `userId` to avoid blank labels.
export function getUserDisplayName(user: { name?: string; userId: string }): string {
	return user.name?.trim() || user.userId
}

//	Like getUserDisplayName, but appends "(userId)" when another user in the
//	`allUsers` pool resolves to the same display name. Used in member-list UI
//	(group sidebar) to disambiguate users who share a display name without
//	cluttering the UI with userIds when names are already unique.
//	`allUsers` should include the target user itself; the function skips the
//	self-match by userId comparison. ha-219.
export function getUserDisplayNameWithDisambiguation(params: {
	target: { name?: string; userId: string }
	allUsers: { name?: string; userId: string }[]
}): string {
	const targetName = getUserDisplayName(params.target)
	const hasCollision = params.allUsers.some(
		u => u.userId !== params.target.userId && getUserDisplayName(u) === targetName
	)
	return hasCollision ? `${targetName} (${params.target.userId})` : targetName
}

export interface UserGroupMembership {
	userId: string
	groupId: string
	status: MembershipStatus
	removeAfter?: number
	bucket: number
	votedToRemoveBy?: string[]
}
